import styles from "./../../styles/charts/chartStyle.module.css";
import { useState, useEffect } from "react";
import BoxPlotChart from "./protoCharts/BoxPlotChart";
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Modal,
  Box,
  Typography,
} from "@mui/material";
import { useAllFundNames, usePropertyData } from "../../lib/hooks";
import { roundNumber } from "../../lib/util";
import {
  getChartBottomIcons,
  getChartInfoIcons,
} from "./twitter/TwitterWordCloud";
import { desc } from "../../lib/chartDescription";

interface VariationOfFundsProps {
  xlabel: string;
  ylabel: string;
  single_fund: boolean;
  property_type?: "Amount" | "Rating" | "Votes";
  // type: "All" | "Funded" | "Not Funded" | "Not Approved" | "Completed";
  popped?: boolean;
}

const voteArray = [
  { name: "Up Votes", val: "YesVotes" },
  { name: "Down Votes", val: "NoVotes" },
  { name: "Result Votes", val: "ResultVotes" },
];

const VariationOfFunds = (props: VariationOfFundsProps) => {
  const {
    xlabel,
    ylabel,
    single_fund,
    // type,
    property_type = "Amount",
  } = props;

  const {
    allFundNames,
    loading: fundNameLoading,
    error: fundNameError,
  } = useAllFundNames();

  const [fund, setFund] = useState<any>(4);

  const [status, setStatus] = useState<
    | "Funded"
    | "Not Funded"
    | "Approved"
    | "Overbudget"
    | "Not Approved"
    | "Completed"
    | "Not Completed"
  >("Funded");

  const [voteType, setVoteType] = useState<
    "YesVotes" | "NoVotes" | "ResultVotes"
  >("YesVotes");

  const [grouping, setGrouping] = useState<"None" | "Binary" | "Ternary">(
    "None"
  );
  const { data, loading } = usePropertyData({
    property_name:
      property_type !== "Votes" ? (property_type as "Amount") : voteType,
    aggregation_type: "List",
    status_grouping: grouping,
    proposal_status: status,
    fund_id: single_fund ? fund : undefined,
  });

  const [seriesData, setSeriesData] = useState<any[]>([]);

  useEffect(() => {
    !fundNameLoading &&
      fundNameError === null &&
      setFund(allFundNames.slice(-1)[0].fund_id);
    //eslint-disable-next-line
  }, [fundNameLoading]);

  const handleChange = (event: any) => {
    setFund(event.target.value);
  };

  const handleChangeStatus = (event: any) => {
    setStatus(event.target.value);
  };

  const handleChangeVotes = (event: any) => {
    setVoteType(event.target.value);
  };
  const handleChangeGrouping = (event: any) => {
    setGrouping(event.target.value);
  };

  useEffect(() => {
    if (loading) {
      setSeriesData([]);
      return;
    } else {
      let data1: { x: any; y: any }[] = [];
      let data2: { x: any; y: any }[] = [];
      let data3: { x: any; y: any }[] = [];

      if (grouping === "None") {
        data.forEach((item: any) => {
          data1.push({
            x: single_fund ? item.challenge_name : item.fund_name,
            y: roundNumber(item.value),
          });
        });
        setSeriesData([
          {
            type: "boxPlot",
            data: data1,
          },
        ]);
      } else if (grouping === "Binary") {
        data.forEach((item: any) => {
          data1.push({
            x: single_fund ? item.challenge_name : item.fund_name,
            y: roundNumber(item.items.funded_value),
          });
          data2.push({
            x: single_fund ? item.challenge_name : item.fund_name,
            y: roundNumber(item.items.not_funded_value),
          });
        });

        setSeriesData([
          {
            type: "boxPlot",
            data: data1,
            name: "Funded",
          },
          {
            type: "boxPlot",
            data: data2,
            name: "Not Funded",
          },
        ]);
      } else {
        data.forEach((item: any) => {
          data1.push({
            x: single_fund ? item.challenge_name : item.fund_name,
            y: roundNumber(item.items.funded_value),
          });
          data2.push({
            x: single_fund ? item.challenge_name : item.fund_name,
            y: roundNumber(item.items.overbudget_value),
          });
          data3.push({
            x: single_fund ? item.challenge_name : item.fund_name,
            y: roundNumber(item.items.not_approved_value),
          });
        });

        setSeriesData([
          {
            type: "boxPlot",
            data: data1,
            name: "Funded",
          },
          {
            type: "boxPlot",
            data: data2,
            name: "Overbudget",
          },
          {
            type: "boxPlot",
            data: data3,
            name: "Not Approved",
          },
        ]);
      }
    }
    //eslint-disable-next-line
  }, [loading]);

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const content = (showMaxButton: boolean) => {
    return (
      <Paper elevation={3} className={styles.chartContainer}>
        <div className="horizontalCenter">
          <Typography className={styles.chartHeader}>
            {grouping !== "None"
              ? `Variation of ${
                  property_type !== "Votes"
                    ? property_type
                    : voteArray.filter((item) => item.val === voteType)[0].name
                }`
              : single_fund
              ? `Variation of ${status} ${
                  property_type !== "Votes"
                    ? property_type
                    : voteArray.filter((item) => item.val === voteType)[0].name
                } in Fund ${fund}`
              : `Variation of ${status}  ${
                  property_type !== "Votes"
                    ? property_type
                    : voteArray.filter((item) => item.val === voteType)[0].name
                }`}
          </Typography>
        </div>
        <div className="horizontalCenter">
          <div className={styles.flexibleControlRow}>
            {single_fund && (
              <FormControl size="small">
                <InputLabel className={styles.flexSelectFont}>Fund</InputLabel>
                <Select
                  className={styles.flexSelectFont}
                  value={fund}
                  label="Fund"
                  onChange={handleChange}
                  defaultValue=""
                  size="small"
                  sx={{ width: 100 }}
                >
                  {allFundNames.map((fund: any) => (
                    <MenuItem
                      style={{ fontSize: "14px" }}
                      value={fund.fund_id}
                      key={`fund-variation-${fund.fund_id}`}
                    >
                      {fund.fund_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small">
              <InputLabel className={styles.flexSelectFont}>
                Grouping Status
              </InputLabel>
              <Select
                className={styles.flexSelectFont}
                value={grouping}
                label="Grouping Status"
                onChange={handleChangeGrouping}
                defaultValue=""
                size="small"
                sx={{ width: 110 }}
              >
                {["None", "Binary", "Ternary"].map((group: any) => (
                  <MenuItem
                    style={{ fontSize: "14px" }}
                    value={group}
                    key={`fund-variation-${group}`}
                  >
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {grouping === "None" && (
              <FormControl size="small">
                <InputLabel className={styles.flexSelectFont}>
                  Proposal Status
                </InputLabel>
                <Select
                  className={styles.flexSelectFont}
                  value={status}
                  label="Proposal Status"
                  onChange={handleChangeStatus}
                  defaultValue="Funded"
                  size="small"
                  sx={{ width: 110 }}
                >
                  {[
                    "Funded",
                    "Not Funded",
                    "Overbudget",
                    "Approved",
                    "Not Approved",
                    "Completed",
                    "Not Completed",
                    "All",
                  ].map((fund: any, index: any) => (
                    <MenuItem
                      style={{ fontSize: "14px" }}
                      value={fund}
                      key={fund}
                    >
                      {fund}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {property_type === "Votes" && (
              <FormControl size="small">
                <InputLabel className={styles.flexSelectFont}>
                  Vote Type
                </InputLabel>
                <Select
                  className={styles.flexSelectFont}
                  value={voteType}
                  label="Vote Type"
                  onChange={handleChangeVotes}
                  defaultValue=""
                  size="small"
                  sx={{ width: 120 }}
                >
                  {voteArray.map((vote: any) => (
                    <MenuItem
                      style={{ fontSize: "14px" }}
                      value={vote.val}
                      key={`fund-variation-${vote.val}`}
                    >
                      {vote.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
        </div>

        <div style={{ width: "100%" }}>
          <BoxPlotChart
            chartData={seriesData}
            xLabel={xlabel}
            yLabel={ylabel}
            hideXLabel={single_fund}
          />
        </div>
        {getChartBottomIcons(showMaxButton, setOpen)}
        {getChartInfoIcons(
          desc["variationOfFunds"][property_type as "Amount"][
            !single_fund ? "fund" : "challenge"
          ]
        )}
      </Paper>
    );
  };
  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={styles.fullScreenChart}>{content(false)}</Box>
      </Modal>
      {content(true)}
    </>
  );
};

export default VariationOfFunds;
